import { ServerCog } from 'lucide-react'
import { useMaintenanceStore } from '@/stores/useMaintenanceStore'
import { Spinner } from '@/components/ui'

const formatWhen = (value?: string): string | undefined =>
  value
    ? new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(
        new Date(value),
      )
    : undefined

/**
 * Full-screen lockout. Shown over everything, including the login page — a user
 * who cannot sign in still deserves to know why.
 */
export function MaintenanceScreen(): React.ReactElement {
  const { message, endsAt } = useMaintenanceStore()
  const back = formatWhen(endsAt)

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-bg-base p-6 dark:bg-gray-900">
      <div className="w-full max-w-md text-center">
        <span className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
          <ServerCog className="h-7 w-7" />
        </span>

        <h1 className="font-display text-2xl font-semibold text-gray-900 dark:text-gray-100">
          We’ll be right back
        </h1>

        <p className="mt-3 text-sm leading-relaxed text-gray-600 dark:text-gray-400">
          {message ?? 'The platform is temporarily unavailable while we carry out maintenance.'}
        </p>

        {back && (
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            Expected back by <span className="font-medium text-gray-700 dark:text-gray-300">{back}</span>
          </p>
        )}

        <div className="mt-8 flex items-center justify-center gap-2 text-xs text-gray-400 dark:text-gray-500">
          <Spinner size="sm" className="!h-3.5 !w-3.5 !border-gray-300 !border-t-transparent" />
          Checking automatically — no need to refresh
        </div>
      </div>
    </div>
  )
}
